import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class AgrofitService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL.endsWith('/api') ? `${API_BASE_URL}/agrofit` : `${API_BASE_URL}/api/agrofit`,
      timeout: 30000,
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      logger.debug('Agrofit request', { url: config.url, method: config.method, params: config.params });
      return config;
    });

    // Interceptor para tratamento de erros
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        logger.error('Agrofit API erro', { status: error.response?.status, url: error.config?.url, data: error.response?.data, message: error.message });
        throw error;
      }
    );
  }

  // Verificar saúde da API
  async checkHealth() {
    try {
      const response = await this.api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao verificar saúde da API: ${error.message}`);
    }
  }

  // Buscar culturas
  async getCulturas() {
    try {
      const response = await this.api.get('/culturas');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar culturas: ${error.message}`);
    }
  }

  // Buscar pragas
  async getPragas() {
    try {
      const response = await this.api.get('/pragas');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar pragas: ${error.message}`);
    }
  }

  // Buscar nomes comuns de pragas
  async getPragasNomesComuns() {
    try {
      const response = await this.api.get('/pragas-nomes-comuns');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar nomes comuns de pragas: ${error.message}`);
    }
  }

  // Buscar plantas daninhas
  async getPlantasDaninhas() {
    try {
      const response = await this.api.get('/plantas-daninhas');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar plantas daninhas: ${error.message}`);
    }
  }

  // Buscar nomes comuns de plantas daninhas
  async getPlantasDaninhasNomesComuns() {
    try {
      const response = await this.api.get('/plantas-daninhas-nomes-comuns');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar nomes comuns de plantas daninhas: ${error.message}`);
    }
  }

  // Buscar ingredientes ativos
  async getIngredientesAtivos() {
    try {
      const response = await this.api.get('/ingredientes-ativos');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar ingredientes ativos: ${error.message}`);
    }
  }

  // Buscar produtos formulados
  async getProdutosFormulados(params = {}) {
    try {
      const response = await this.api.get('/produtos-formulados', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar produtos formulados: ${error.message}`);
    }
  }

  // Busca avançada de produtos formulados (suporta filtro por nome do produto)
  async searchProdutosFormulados(params = {}) {
    try {
      const response = await this.api.get('/search/produtos-formulados', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Erro na busca de produtos formulados: ${error.message}`);
    }
  }

  // Buscar produto formulado por registro
  async getProdutoFormuladoByRegistro(registro) {
    try {
      const response = await this.api.get(`/produtos-formulados/${registro}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar produto formulado: ${error.message}`);
    }
  }


  async getMarcasComerciais(params = {}) {
    try {
      const response = await this.api.get('/marcas-comerciais', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar marcas comerciais: ${error.message}`);
    }
  }

  // Buscar classificações toxicológicas
  async getClassificacaosToxicologicas() {
    try {
      const response = await this.api.get('/classificacoes-toxicologicas');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar classificações toxicológicas: ${error.message}`);
    }
  }

  // Buscar classificações ambientais
  async getClassificacoesAmbientais() {
    try {
      const response = await this.api.get('/classificacoes-ambientais');
      return response.data;
    } catch (error) {
      throw new Error(`Erro ao buscar classificações ambientais: ${error.message}`);
    }
  }

  // Métodos de busca avançada removidos por desativação da página Agrofit

  // Método auxiliar para buscar dados básicos necessários para formulários
  async getDadosBasicos() {
    try {
      const [culturas, classificacoesTox, classificacoesAmb] = await Promise.all([
        this.getCulturas(),
        this.getClassificacaosToxicologicas(),
        this.getClassificacoesAmbientais()
      ]);

      return {
        culturas,
        pragas: [],
        classificacoesToxicologicas: classificacoesTox,
        classificacoesAmbientais: classificacoesAmb
      };
    } catch (error) {
      // Fallback em desenvolvimento: dados mockados para permitir testes da UI
      if (process.env.NODE_ENV !== 'production') {
        logger.warn('Agrofit: usando dados básicos mockados');
        return {
          culturas: [
            { nome: 'Soja' },
            { nome: 'Milho' },
            { nome: 'Algodão' },
            { nome: 'Cana-de-açúcar' }
          ],
          pragas: [
            { nome: 'Lagarta-do-cartucho' },
            { nome: 'Ferrugem asiática' },
            { nome: 'Percevejo' },
            { nome: 'Mosca-branca' }
          ],
          classificacoesToxicologicas: [
            { nome: 'I - Extremamente Tóxico' },
            { nome: 'II - Altamente Tóxico' },
            { nome: 'III - Moderadamente Tóxico' },
            { nome: 'IV - Pouco Tóxico' }
          ],
          classificacoesAmbientais: [
            { nome: 'I - Perigoso ao Meio Ambiente' },
            { nome: 'II - Muito Perigoso ao Meio Ambiente' },
            { nome: 'III - Perigoso ao Meio Ambiente' },
            { nome: 'IV - Pouco Perigoso ao Meio Ambiente' }
          ]
        };
      }
      throw new Error(`Erro ao buscar dados básicos: ${error.message}`);
    }
  }
}

export default new AgrofitService();
